/* eslint-disable react/no-unstable-nested-components */
import * as React from "react";
import { Alert, Pagination, Table } from "@jhool-io/fe-components";
import { useSearchParams } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import { useGetPayments } from "../../hooks/payments.queries";
import { IPayment } from "../../types/payments.types";
import Skeleton from "../../../../components/Skeleton/Skeleton";
import ListState from "../../../../components/ListState/ListState";
import {
    formatDate,
    formatNoteType,
    showMoneyInAppFormat,
} from "../../../../utils/helpers";

export default function PaymentsHistory() {
    const [searchParams, setSearchParams] = useSearchParams();

    const pageFilter = Number(searchParams.get("page")) || 1;
    const limitFilter = Number(searchParams.get("limit")) || 20;

    const { data, error, isLoading, isSuccess } = useGetPayments({
        page: pageFilter,
        limit: limitFilter,
    });

    const columns: ColumnDef<IPayment>[] = [
        {
            accessorKey: "date_of_service",
            header: "Date of Service",
            cell: ({ row }) => formatDate(row.original.date_of_service, true),
        },
        {
            accessorKey: "payment_date",
            header: "Payment Date",
            cell: ({ row }) => formatDate(row.original.payment_date, true),
        },
        {
            accessorKey: "payment_status",
            header: "Status",
            cell: ({ row }) => (
                <span className="capitalize">
                    {row.original.payment_status}
                </span>
            ),
        },
        {
            accessorKey: "note_type",
            header: "Service Type",
            cell: ({ row }) => (
                <span className="capitalize">
                    {formatNoteType(row.original.note_type)}
                </span>
            ),
        },
        {
            accessorKey: "amount_paid",
            header: "Amount Paid",
            cell: ({ row }) => showMoneyInAppFormat(row.original.amount_paid),
        },
        {
            accessorKey: "card",
            header: "Card",
        },
    ];

    const handlePageChange = (page: number) => {
        searchParams.set("page", String(page));
        setSearchParams(searchParams);
    };

    const handleLimitChange = (limit: number) => {
        searchParams.set("limit", String(limit));
        setSearchParams(searchParams);
    };

    return (
        <div>
            {error && (
                <ListState
                    isError
                    stateHelperText="Please try again later"
                    errorMsg={
                        error.response?.data.message ||
                        "Error loading payments history"
                    }
                />
            )}
            {isLoading && (
                <Skeleton type="table" containerTestId="payments-loader" />
            )}

            {isSuccess && data && (
                <>
                    <Alert
                        type="info"
                        description="Here you can view the history and details of your past transactions."
                        classNames=" mb-12"
                    />
                    {data.data.length === 0 && (
                        <ListState
                            emptyMessage="No payments history yet"
                            stateHelperText="Payments history will appear here when available"
                            isError={false}
                        />
                    )}
                    {data.data.length > 0 && (
                        <Table
                            columns={columns}
                            data={data.data}
                            hasPagination={data.total_count > 20}
                            pagination={
                                <Pagination
                                    totalCount={data.total_count}
                                    totalPages={data.total_pages}
                                    currentPage={pageFilter}
                                    onPageChange={handlePageChange}
                                    limit={limitFilter}
                                    onLimitChange={handleLimitChange}
                                />
                            }
                        />
                    )}
                </>
            )}
        </div>
    );
}
